import http from './axiosInstance'
import errorHandler from '../transformers/axiosErrors'
import {
  ApiResponse,
  Interview,
  JD,
  Note,
  OpportunityDTO,
  TrackerStage,
} from '@/types'
import { removeNulls } from '../helpers'

interface OpportunityOptions {
  page?: number | null
  archived?: boolean | null
  perPage?: number | null
  search?: string | null
  sortDir?: string | null
  sortBy?: string | null
  stage?: string | null
}

const defaultOptions: OpportunityOptions = {
  page: 1,
  archived: false,
  perPage: 50,
  search: null,
  sortDir: null,
  sortBy: null,
  stage: null,
}

export const fetchOpportunities = async (
  options = {}
): Promise<ApiResponse> => {
  const mergedOptions = { ...defaultOptions, ...options }
  const resp: ApiResponse = { data: null, error: null }
  try {
    const response = await http.get(`/opportunities`, { params: mergedOptions })
    resp.data = response.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}

export const fetchOpportunity = async (id): Promise<ApiResponse> => {
  const resp: ApiResponse = { data: null, error: null }
  try {
    const response = await http.get(`/opportunities/${id}`)
    resp.data = response.data.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}

export const createOpportunity = async (opportunity): Promise<ApiResponse> => {
  opportunity.stage = opportunity.stage || TrackerStage.SAVED
  const resp: ApiResponse = { data: null, error: null }
  try {
    const response = await http.post('/opportunities', opportunity)
    resp.data = response.data.data
  } catch (e) {
    if (e.response && e.response.status === 409) {
      resp.error = {
        message: "You have already saved this job.",
        opportunity: e.response.data.errors?.duplicate_opportunity?.opportunity || {}
      }
    } else {
      resp.error = e.isAxiosError ? errorHandler(e) : e
    }
  }
  return resp
}

export const updateOpportunity = async (
  opportunity: OpportunityDTO
): Promise<ApiResponse> => {
  const resp: ApiResponse = { data: null, error: null }
  try {
    const response = await http.put(
      `/opportunities/${opportunity.id}`,
      opportunity
    )
    resp.data = response.data.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}

export const deleteOpportunity = async (
  opportunityId: Number
): Promise<ApiResponse> => {
  const resp: ApiResponse = { data: null, error: null }
  try {
    const response = await http.delete(`/opportunities/${opportunityId}`)
    resp.data = response.data.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}

export const createJd = async (jd: JD): Promise<ApiResponse> => {
  const resp: ApiResponse = { data: null, error: null }
  try {
    const response = await http.post(`/jds`, jd)
    resp.data = response.data.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}

export const updateJD = async (jd: JD): Promise<ApiResponse> => {
  const resp: ApiResponse = { data: null, error: null }
  try {
    const response = await http.put(`/jds/${jd.id}`, jd)
    resp.data = response.data.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}

export const createInterview = async (
  opportunity_id: Number,
  interview: Interview
): Promise<ApiResponse> => {
  const resp: ApiResponse = { data: null, error: null }
  try {
    interview.when = new Date(interview.when).toISOString()
    const data = Object.assign(interview, { opportunity_id })
    const response = await http.post(`/interviews`, data)
    resp.data = response.data.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}

export const updateInterview = async (
  interview: Interview
): Promise<ApiResponse> => {
  const resp: ApiResponse = { data: null, error: null }
  interview = removeNulls(interview) as Interview
  try {
    interview.when = new Date(interview.when).toISOString()
    const response = await http.put(`/interviews/${interview.id}`, interview)
    resp.data = response.data.data
    resp.data.when = new Date(resp.data.when)
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}

export const deleteInterview = async (
  interviewId: Number
): Promise<ApiResponse> => {
  const resp: ApiResponse = { data: null, error: null }
  try {
    const response = await http.delete(`/interviews/${interviewId}`)
    resp.data = response.data.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}

export const createNote = async (
  opportunity_id: Number,
  note: Note
): Promise<ApiResponse> => {
  const resp: ApiResponse = { data: null, error: null }
  try {
    const data = Object.assign(note, { opportunity_id })
    const response = await http.post(`/notes`, data)
    resp.data = response.data.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}

export const updateNote = async (note: Note): Promise<ApiResponse> => {
  const resp: ApiResponse = { data: null, error: null }
  try {
    const response = await http.put(`/notes/${note.id}`, note)
    resp.data = response.data.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}

export const deleteNote = async (noteId: Number): Promise<ApiResponse> => {
  const resp: ApiResponse = { data: null, error: null }
  try {
    const response = await http.delete(`/notes/${noteId}`)
    resp.data = response.data.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}

export const fetchScanHistory = async (): Promise<ApiResponse> => {
  const resp: ApiResponse = { data: null, error: null }
  try {
    const response = await http.get(`/results`)
    resp.data = response.data.data
  } catch (e) {
    resp.error = e.isAxiosError ? errorHandler(e) : e
  }
  return resp
}
